import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Link2Off, CheckCircle2, Loader2, XCircle, MapPin } from "lucide-react";

const REGISTRY_LABEL = { incometax: "Income Tax (PAN)", darpan: "NGO Darpan", fcra: "FCRA" };

export default function Verification() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/v1/verify/queue");
      setItems(r.data.items);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const resolve = async (body, busyKey) => {
    setBusy(busyKey);
    try {
      await api.post("/v1/verify/resolve", body);
      await load();
    } finally {
      setBusy(null);
    }
  };

  const multiDarpan = items.filter((it) => it.type === "multi_darpan");
  const givedo = items.filter((it) => it.type === "givedo_crossstate");

  return (
    <div>
      <PageHeader
        eyebrow="Admin · crosswalk verification"
        title="Verification Queue"
        subtitle="Entities and links needing a human check — multiple Darpan registrations on one PAN, and give.do cross-state links. Every action updates the database immediately."
      >
        <span data-testid="verify-count" className="rounded-full border border-slate-700 px-3 py-1.5 font-mono text-xs text-slate-300">{items.length} pending</span>
      </PageHeader>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div data-testid="verify-empty" className="text-sm text-slate-500">Nothing to verify — all clear.</div>
      ) : (
        <div className="space-y-10">
          {/* Multiple Darpan registrations */}
          <section data-testid="verify-section-multidarpan">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-400">
              Multiple Darpan registrations <span className="ml-1 font-mono text-xs text-slate-500">({multiDarpan.length})</span>
            </h2>
            {multiDarpan.length === 0 ? (
              <div className="text-xs text-slate-600">None pending.</div>
            ) : (
              <div className="space-y-4">
                {multiDarpan.map((it) => (
                  <div key={it.entity_key} data-testid={`verify-item-${it.entity_key}`} className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-100">{it.name}</div>
                        <div className="font-mono text-[11px] text-slate-500">{it.pan} · {it.state} · {it.darpans.length} Darpan registrations</div>
                      </div>
                      <button
                        data-testid={`verify-confirm-${it.entity_key}`}
                        onClick={() => resolve({ entity_key: it.entity_key, action: "confirm" }, `${it.entity_key}:confirm`)}
                        disabled={busy === `${it.entity_key}:confirm`}
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 transition-colors duration-150"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" /> Mark verified
                      </button>
                    </div>
                    <div className="space-y-2">
                      {it.darpans.map((d) => (
                        <div key={d.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-800 px-3 py-2">
                          <div className="min-w-0">
                            <span className="font-mono text-xs text-purple-400">Darpan · {d.id}</span>
                            <span className="ml-2 text-xs text-slate-400">{d.ngo_name} · {d.state}</span>
                            {!d.is_best && <span className="ml-2 rounded border border-amber-400/40 bg-amber-400/10 px-1.5 py-0.5 text-[10px] font-semibold text-amber-400">not best match</span>}
                          </div>
                          <button
                            data-testid={`verify-detach-${it.entity_key}-${d.id.replace(/\W/g, "")}`}
                            onClick={() => resolve({ entity_key: it.entity_key, action: "detach_darpan", darpan_id: d.id }, `${it.entity_key}:${d.id}`)}
                            disabled={busy === `${it.entity_key}:${d.id}`}
                            className="inline-flex shrink-0 items-center gap-1 rounded-full border border-rose-400/50 px-3 py-1 text-xs font-semibold text-rose-400 hover:bg-rose-400/10 disabled:opacity-50 transition-colors duration-150"
                          >
                            <Link2Off className="w-3.5 h-3.5" /> Detach
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* give.do cross-state links */}
          <section data-testid="verify-section-givedo">
            <h2 className="mb-1 text-sm font-semibold uppercase tracking-wide text-slate-400">
              give.do cross-state links <span className="ml-1 font-mono text-xs text-slate-500">({givedo.length})</span>
            </h2>
            <p className="mb-3 text-xs text-slate-500 max-w-2xl">
              A give.do reference attached a registration from a different state to a shared PAN. Accept keeps the link (marks it verified); Reject unlinks it.
            </p>
            {givedo.length === 0 ? (
              <div className="text-xs text-slate-600">None pending.</div>
            ) : (
              <div className="space-y-4">
                {givedo.map((it) => {
                  const bk = it.id;
                  const mismatch = it.left?.state && it.right?.state &&
                    it.left.state.trim().toLowerCase() !== it.right.state.trim().toLowerCase();
                  return (
                    <div key={it.id} data-testid={`verify-givedo-${it.id.replace(/\W/g, "")}`} className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                        <span className="font-mono text-[11px] text-slate-500">{it.pair_type} link · possible match, not verified</span>
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            data-testid={`verify-accept-${it.id.replace(/\W/g, "")}`}
                            onClick={() => resolve({ action: "accept_link", pair_type: it.pair_type, left_id: it.left_id, right_id: it.right_id }, `${bk}:accept`)}
                            disabled={!!busy}
                            className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 transition-colors duration-150"
                          >
                            {busy === `${bk}:accept` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Accept (keep link)
                          </button>
                          <button
                            data-testid={`verify-reject-${it.id.replace(/\W/g, "")}`}
                            onClick={() => resolve({ action: "reject_link", pair_type: it.pair_type, left_id: it.left_id, right_id: it.right_id }, `${bk}:reject`)}
                            disabled={!!busy}
                            className="inline-flex shrink-0 items-center gap-1 rounded-full border border-rose-400/50 px-3 py-1.5 text-xs font-semibold text-rose-400 hover:bg-rose-400/10 disabled:opacity-50 transition-colors duration-150"
                          >
                            {busy === `${bk}:reject` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />} Reject (unlink)
                          </button>
                        </div>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        {[it.left, it.right].map((side, i) => (
                          <div key={i} className="min-w-0 overflow-hidden rounded-lg border border-slate-800 px-3 py-2.5">
                            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{REGISTRY_LABEL[side?.registry] || side?.registry}</div>
                            <div className="mt-0.5 text-sm text-slate-200 truncate">{side?.name || "—"}</div>
                            <div className="font-mono text-[11px] text-slate-500 truncate">{side?.id}</div>
                            {side?.state && (
                              <div className={`mt-1 inline-flex items-center gap-1 text-[11px] ${mismatch ? "text-amber-400" : "text-slate-400"}`}>
                                <MapPin className="w-3 h-3" /> {side.state}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      {mismatch && (
                        <div className="mt-3 rounded-md border border-amber-400/40 bg-amber-400/10 px-3 py-2 text-[11px] text-amber-300">
                          State mismatch between the two registrations — likely an incorrect give.do link.
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
